import {
  DumbbellIcon,
  EvChargingStationIcon,
  HasBabyCarraige,
  HasParking,
  HasPool,
  HasSpa,
  HasWifi,
  KidsFriendlyIcon,
  ParkingIcon,
  PetFriendlyIcon,
  SpaWellnessIcon,
} from "@/app/components/common/Icons";
import { publicEnvConfig } from "../config/config";
import type { useTranslations } from "next-intl";
/**
 * This constant is used to set the value of payment type.
 */
export const PAYMENT_TYPES = {
  CREDIT_CARD: "creditCard",
  CRYPTO: "crypto",
} as const;

export const AmenityIcons: { [key: string]: string } = {
  "Non-Smoking Facility": "no-smoking.svg",
  "Cable/Satellite TV": "television.svg",
  "Hair Dryer": "hair-dryer.svg",
  "Flat Screen TV": "television.svg",
  "Coffee Maker": "coffee-maker.svg",
  "Alarm Clock": "alarm.svg",
  "Internet Access - Free in room": "wifi-icon.svg",
  "Internet Access - in room for fee": "wifi-icon.svg",
  "Wheelchair Access": "wheelchair.svg",
  "In room safe": "safe-in-room.svg",
  "Mini-Bar": "mini-bar-icon.svg",
  Refrigerator: "refrigerator.svg",
  Balcony: "balcony.svg",
  Microwave: "microwave.svg",
} as const;

export const TRAVELER_TYPES = ["adults", "children", "infants"];

export const getTravelerOptions = (t: ReturnType<typeof useTranslations>) => [
  { label: t("HomePageFlights.adults"), value: "2", description: "17+" },
  { label: t("HomePageFlights.children"), value: "0", description: "2 - 17" },
  { label: t("HomePageFlights.infants"), value: "0", description: "<2" },
];

export const SEARCH_PARAMS = {
  CITY: "city",
  COUNTRY: "country",
  DESTINATION: "destination",
  CHECKIN: "checkIn",
  CHECKOUT: "checkOut",
  ADULTS: "adults",
  CHILDREN: "children",
  INFANTS: "infants",
  CURRENCY: "currency",
  ID: "id",
};

export const MIN_SEARCH_LENGTH = 2;
export const MAX_TRAVELER_COUNT = 10;

export const DATE_FORMAT = "DD-MM-YY";
export const API_DATE_FORMAT = "YYYY-MM-DD";

export const MAX_SEARCH_TIMEOUT_IN_MS = 45000;
export const MAX_REMINDER_TIMEOUT_IN_MS = 1800000;
export const MAX_REMINDER_INTERVAL_IN_MS = 1200000;

export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thur", "Fri", "Sat"];

export const WALLET_HINTS = [
  {
    label: "Sign In",
    description:
      "Securely sign in to unlock a world of exclusive benefits, including special hotel offers tailored for Web3 users.",
    icon: "/assets/v2/icons/signin.svg",
  },
  {
    label: "Get Personal Offers",
    description:
      "Explore a curated selection of exclusive hotel offers, accessible only to wallet-connected users. Enjoy unbeatable rates and personalized deals.",
    icon: "/assets/v2/icons/personal-offer.svg",
  },
  {
    label: "Book & Check-out",
    description:
      "Seamlessly book your preferred accommodation and complete your check out process. Choose to pay conveniently with over 200+ cryptocurrency options, making your travel experience as smooth as possible.",
    icon: "/assets/v2/icons/book-and-checkout.svg",
  },
];

const getDestinationId = (id1: string, id2: string): string => {
  return publicEnvConfig?.env === "dev" ? id1 : id2;
};

export const TOP_DESTINATIONS = [
  {
    image: "/assets/v2/images/barcelona.jpg",
    city: "Barcelona",
    countryName: "Spain",
    countryCode: "ES",
    description:
      "Barcelona captivates with its breathtaking architecture, vibrant street life, beautiful beaches, mouthwatering cuisine, and a lively arts and culture scene.",
    destination: "Barcelona & surrounding area",
    tag: "Very Popular",
    id: getDestinationId("C27853", "C27790"),
  },
  {
    image: "/assets/v2/images/rome.jpg",
    city: "Rome",
    countryName: "Italy",
    countryCode: "IT",
    destination: "Latium",
    description:
      "Rome offers ancient ruins, stunning architecture, delicious cuisine, vibrant piazzas, and a unique blend of history and modernity.",
    tag: "New Hotels",
    id: getDestinationId("C65657", "C65599"),
  },
  {
    image: "/assets/v2/images/paris.jpg",
    city: "Paris",
    countryName: "France",
    countryCode: "FR",
    destination: "Île de France",
    description:
      "Paris is a must-visit for its iconic landmarks, rich history, art, culture, culinary delights, and romantic atmosphere.",
    tag: "Very Popular",
    id: getDestinationId("C51589", "C51528"),
  },
  {
    image: "/assets/v2/images/amsterdam.jpg",
    city: "Amsterdam",
    countryName: "Netherlands",
    countryCode: "NL",
    destination: "Amsterdam & surrounding area",
    description:
      "Amsterdam charms with its picturesque canals, historic architecture, world-class museums, vibrant cycling culture, and a relaxed atmosphere perfect for exploration.",
    id: getDestinationId("C74894", "C74839"),
  },
  {
    image: "/assets/v2/images/berlin.jpg",
    city: "Berlin",
    countryName: "Germany",
    countryCode: "DE",
    destination: "Berlin",
    description:
      "Berlin beckons with its fascinating history, vibrant nightlife, diverse art scene, multicultural atmosphere, and a thriving alternative and subculture.",
    id: getDestinationId("C21066", "C21003"),
  },
  {
    image: "/assets/v2/images/london.jpg",
    city: "London",
    countryName: "United Kingdom",
    countryCode: "GB",
    destination: "London & surrounding area",
    description:
      "London entices with its iconic landmarks, world-class museums, vibrant theater scene, diverse culinary offerings, and a blend of old-world charm and modern dynamism.",
    tag: "New Hotels",
    id: getDestinationId("C35216", "C35155"),
  },
  {
    image: "/assets/v2/images/prague.jpg",
    city: "Prague",
    countryName: "Czech Republic",
    countryCode: "CZ",
    destination: "Prague & surrounding area",
    description:
      "Prague enchants with its fairy-tale-like architecture, rich history, charming cobblestone streets, hearty cuisine, and a bohemian ambiance that is truly captivating.",
    id: getDestinationId("C19427", "C19343"),
  },
  {
    image: "/assets/v2/images/vienna.jpg",
    city: "Vienna",
    countryName: "Austria",
    countryCode: "AT",
    destination: "Vienna & surrounding area",
    description:
      "Vienna allures with its elegant palaces, classical music heritage, captivating art and culture scene, delectable coffeehouse culture, and architectural grandeur at every turn.",
    tag: "New Hotels",
    id: getDestinationId("C4934", "C4834"),
  },
  {
    image: "/assets/v2/images/lisbon.jpg",
    city: "Lisbon",
    countryName: "Portugal",
    countryCode: "PT",
    destination: "Lisbon Region and Setúbal",
    description:
      "Lisbon beckons with its colorful tiled facades, charming neighborhoods, delicious seafood, Fado music, and breathtaking views from its hilly streets and viewpoints.",
    id: getDestinationId("C82331", "C82276"),
  },
  {
    image: "/assets/v2/images/budapest.jpg",
    city: "Budapest",
    countryName: "Hungary",
    countryCode: "HU",
    destination: "Budapest & surrounding area",
    description:
      "Budapest captivates with its stunning architecture, thermal baths, vibrant nightlife, rich history, delicious cuisine, and the picturesque Danube River flowing through the city.",
    tag: "New Hotels",
    id: getDestinationId("C54336", "C54274"),
  },
  {
    image: "/assets/v2/images/istanbul.jpg",
    city: "Istanbul",
    countryName: "Turkey",
    countryCode: "TR",
    destination: "Istanbul (province)",
    description:
      "Istanbul mesmerizes with its magnificent mosques, bustling bazaars, rich history, delightful cuisine, vibrant street life, and the enchanting blend of East and West.",
    id: getDestinationId("C89170", "C89117"),
  },
  {
    image: "/assets/v2/images/athens.jpg",
    city: "Athens",
    countryName: "Greece",
    countryCode: "GR",
    description:
      "Athens allures with its ancient wonders like the Acropolis, fascinating mythology, Mediterranean charm, delicious cuisine, and vibrant modern energy.",
    destination: "Attica (Athens and surrounding area)",
    tag: "Very Popular",
    id: getDestinationId("C52150", "C52089"),
  },
  {
    image: "/assets/v2/images/stockholm.jpg",
    city: "Stockholm",
    countryName: "Sweden",
    destination: "Stockholm & surrounding area",
    countryCode: "SE",
    description:
      "Stockholm delights with its scenic waterways, picturesque islands, historic architecture, Nordic design, rich cultural heritage, and vibrant culinary scene.",
    id: getDestinationId("C86948", "C86895"),
  },
  {
    image: "/assets/v2/images/florence.jpg",
    city: "Florence",
    countryName: "Italy",
    countryCode: "IT",
    destination: "Tuscany",
    description:
      "Florence enchants with its Renaissance masterpieces, iconic Duomo, romantic atmosphere, world-class art galleries, delectable cuisine, and charming streets lined with history.",
    tag: "Very Popular",
    id: getDestinationId("C69125", "C69068"),
  },
  {
    image: "/assets/v2/images/dublin.jpg",
    city: "Dublin",
    countryName: "Ireland",
    countryCode: "IE",
    destination: "Dublin & surrounding area",
    description:
      "Dublin charms with its lively pub culture, warm hospitality, rich literary heritage, historic landmarks, traditional music, and vibrant street life.",
    tag: "New Hotels",
    id: getDestinationId("C3", "C54283"), // city not found
  },
  {
    image: "/assets/v2/images/krakow.jpg",
    city: "Krakow",
    countryName: "Poland",
    countryCode: "PL",
    destination: "Lesser Poland Voivodeship",
    description:
      "Krakow enthralls with its well-preserved medieval archi-tecture, poignant history, vibrant cultural scene, charming old town, and proximity to Auschwitz-Birkenau Memorial and Museum.",
    id: getDestinationId("C79904", "C79849"),
  },
  {
    image: "/assets/v2/images/copenhagen.jpg",
    city: "Copenhagen",
    countryName: "Denmark",
    countryCode: "DK",
    destination: "Copenhagen",
    description:
      "Copenhagen captivates with its modern design, charming canals, world-renowned cuisine, cycling culture, beautiful parks, and a cozy hygge atmosphere.",
    tag: "New Hotels",
    id: getDestinationId("C17292", "C17234"),
  },
  {
    image: "/assets/v2/images/edinburgh.jpg",
    city: "Edinburgh",
    countryName: "United Kingdom",
    countryCode: "GB",
    destination: "Scotland",
    description:
      "Edinburgh enchants with its medieval Old Town, majestic castle, lively festivals, haunting history, scenic landscapes, and thriving arts and literary scene.",
    id: getDestinationId("C37156", "C37089"),
  },
  {
    image: "/assets/v2/images/new-york.jpg",
    city: "New York",
    countryName: "United States of America",
    destination: "New York",
    countryCode: "US",
    description:
      "New York dazzles with its iconic landmarks, vibrant cityscape, diverse neighborhoods, world-class cuisine, unmatched shopping, and unparalleled cultural offerings.",
    tag: "Very Popular",
    id: getDestinationId("C3", "C96604"), // city not found on the list
  },
  {
    image: "/assets/v2/images/dubai.jpg",
    city: "Dubai",
    countryName: "United Arab Emirates",
    destination: "Dubai",
    countryCode: "AE",
    description:
      "Dubai amazes with its futuristic architecture, luxurious shopping, stunning skyline, world-class entertainment, desert adventures, and a melting pot of cultures.",
    tag: "Very Popular",
    id: getDestinationId("C63", "C66"),
  },
];

export const HOTEL_SUMMARY = [
  "Amazing hotel",
  "Very clean and tidy",
  "Wonderful rooms",
  "Great value for money",
  "Excellent service",
];

export const OPTIONS: DateTimeFormatOptions = {
  weekday: "short",
  month: "short",
  day: "numeric",
};

export const REVIEWS = [
  {
    img: "/assets/v2/images/Frame 964-7.jpg",
    review: "Effortless booking with Web3 - secure and easy!",
  },
  {
    img: "/assets/v2/images/Frame 964-8.jpg",
    review: "Web3 tech rocks for hotel bookings!",
  },
  {
    img: "/assets/v2/images/Frame 964-9.jpg",
    review: "Quick and easy hotel booking with Web3. Love it",
  },
  {
    img: "/assets/v2/images/Frame 964-10.jpg",
    review: "No hidden fees, just honest prices. Great!",
  },
  {
    img: "/assets/v2/images/Frame 964-11.jpg",
    review: "Last-minute trip? Try Sleap. Quick and crypto-friendly.",
  },
  {
    img: "/assets/v2/images/Frame 964-12.jpg",
    review: "Bye, middlemen! Hello, Web3 hotels!",
  },
];

export const LOGO_LISTS = [
  { src: "/assets/v2/images/MBA_1x1.png", alt: "Made by Apes" },
  { src: "/assets/v2/images/Calque 2.png", alt: "World of Women" },
  { src: "/assets/v2/images/animoca.png", alt: "Animoca" },
  { src: "/assets/v2/images/Camino.png", alt: "Camino" },
  {
    src: "/assets/v2/images/Forbesweb3logo_146x127.png",
    alt: "Forbesweb3",
    width: 112,
    height: 31,
  },
  { src: "/assets/v2/images/Frame 1154.png", alt: "rug radio" },
  {
    src: "/assets/v2/images/poweredbyape.png",
    alt: "ape coin",
    width: 146,
    height: 42,
  },
];

export const FEATUIRED_LISTS = [
  "/assets/v2/images/Group.png",
  "/assets/v2/images/techstartups.png",
  "/assets/v2/images/Yahoo-Logo.png",
  "/assets/v2/images/schema-publisher-logo-benzinga.png",
  "/assets/v2/images/278927736_305625345061589_7627463802972348540_n.png",
  "/assets/v2/images/Frame-11 1.png",
  "/assets/v2/images/2560px-Bloomberg_logo.png",
];

export const FEATUIRED_LISTS_LIGHT = [
  "/assets/v2/images/Frame1.png",
  "/assets/v2/images/Frame2.png",
  "/assets/v2/images/Frame3.png",
  "/assets/v2/images/Frame4.png",
  "/assets/v2/images/Frame5.png",
  "/assets/v2/images/Frame6.png",
  "/assets/v2/images/Frame7.png",
];

export const PAGES_LINKS = {
  HOMEPAGE: "https://sleap.io",
  BENEFITS: "https://sleap.io/benefits",
  ABOUT: "https://sleap.io/about",
  HELP: "https://sleap.io/faq",
  DISCORD: "https://discord.com/invite/sleap",
  NFT: "https://sleap.io/nft",
  POINTS: "https://sleap.io/points",
};
export const CARD_ICONS = [
  { calling: "hasSpa", icon: HasSpa },
  { calling: "hasParking", icon: HasParking },
  { calling: "hasFamilyRooms", icon: HasBabyCarraige },
  { calling: "hasWifi", icon: HasWifi },
  { calling: "hasPool", icon: HasPool },
  {
    calling: "hasEVChargingStation",
    icon: EvChargingStationIcon,
  },
  { calling: "isPetFriendly", icon: PetFriendlyIcon },
  { calling: "hasGym", icon: DumbbellIcon },
];

export const WEEKDAYSCALENDAR = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export enum HotelRoomOfferBoardType {
  none = "none",
  breakfast = "Breakfast included",
  allInclusive = "All Inclusive",
  americanBreakfast = "American breakfast",
  asianBreakfast = "Asian breakfast",
  breakfastIncluded = "Breakfast included",
  breakfastBuffet = "Breakfast Buffet",
  breakfastFor1 = "Breakfast for 1 guest",
  breakfastFor2 = "Breakfast for 2 guests",
  chineseBreakfast = "Chinese breakfast",
  continentalBreakfast = "Continental Breakfast",
  englishBreakfast = "English breakfast",
  dinner = "Dinner",
  fullBoard = "Breakfast, lunch and dinner included",
  halfBoard = "Breakfast and dinner or lunch included",
  halfBoardDinner = "Breakfast and dinner",
  halfBoardLunch = "Breakfast and lunch",
  irishBreakfast = "Irish breakfast",
  israeliBreakfast = "Israeli breakfast",
  japaneseBreakfast = "Japanese breakfast",
  lunch = "Lunch",
  scandinavianBreakfast = "Scandinavian breakfast",
  scottishBreakfast = "Scottish breakfast",
  softAllInclusive = "Soft all inclusive",
  someMeal = "Meals included",
  superAllInclusive = "Super all inclusive",
  ultraAllInclusive = "Ultra all inclusive",
}

export const BOARD_MAPPING = [
  {
    code: "1",
    key: "All_inclusive",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.allInclusive,
  },
  {
    code: "2",
    key: "American",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    code: "3",
    key: "Bed_And_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.allInclusive,
  },
  {
    code: "4",
    key: "Buffet_And_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    code: "5",
    key: "Caribbean_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    code: "6",
    key: "Continental_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    code: "7",
    key: "English_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "8",
    key: "European_plan",
    text: "",
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "9",
    key: "Family_plan",
    text: "",
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "10",
    key: "Full_board",
    text: HotelRoomOfferBoardType.fullBoard,
  },
  {
    code: "11",
    key: "Full_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "12",
    key: "Half_board",
    text: HotelRoomOfferBoardType.halfBoard,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "13",
    key: "As_brochured",
    text: "",
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "14",
    key: "Room_only",
    text: HotelRoomOfferBoardType.none,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "15",
    key: "Self_catering",
    text: "",
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "16",
    key: "Bermuda",
    text: "",
  },
  {
    code: "17",
    key: "Dinner_And_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.halfBoardDinner,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "18",
    key: "Family_American",
    text: "",
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "19",
    key: "Breakfast",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "20",
    key: "Modified",
    text: "",
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "255",
    key: "Full_board_And_drink",
    text: HotelRoomOfferBoardType.fullBoard,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "256",
    key: "Half_board_And_drink",
    text: HotelRoomOfferBoardType.halfBoard,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "257",
    key: "All_Inclusive_Premium",
    text: HotelRoomOfferBoardType.superAllInclusive,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "258",
    key: "Gala_Dinner",
    text: HotelRoomOfferBoardType.dinner,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "259",
    key: "All_Inclusive_Light",
    text: HotelRoomOfferBoardType.softAllInclusive,
  },
  {
    code: "260",
    key: "Bed_And_breakfast_And_Half_board",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.allInclusive,
  },
  {
    code: "free_breakfast",
    key: "free_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    code: "room_only",
    key: "room_only",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.none,
  },
  {
    code: "all-inclusive",
    key: "all-inclusive",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.allInclusive,
  },
  {
    code: "american-breakfast",
    key: "american-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.americanBreakfast,
  },
  {
    code: "asian-breakfast",
    key: "asian-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.asianBreakfast,
  },
  {
    code: "breakfast",
    key: "breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfastIncluded,
  },
  {
    code: "breakfast-buffet",
    key: "breakfast-buffet",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfastBuffet,
  },
  {
    code: "breakfast-for-1",
    key: "breakfast-for-1",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfastFor1,
  },
  {
    code: "breakfast-for-2",
    key: "breakfast-for-2",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfastFor2,
  },
  {
    code: "chinese-breakfast",
    key: "chinese-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.chineseBreakfast,
  },
  {
    code: "continental-breakfast",
    key: "continental-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.continentalBreakfast,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "dinner",
    key: "dinner",
    text: HotelRoomOfferBoardType.dinner,
  },
  {
    code: "english-breakfast",
    key: "english-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.englishBreakfast,
  },
  {
    code: "full-board",
    key: "full-board",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.fullBoard,
  },
  {
    code: "half-board",
    key: "half-board",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.halfBoard,
  },
  {
    code: "half-board-dinner",
    key: "half-board-dinner",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.halfBoardDinner,
  },
  {
    code: "half-board-lunch",
    key: "half-board-lunch",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.halfBoardLunch,
  },
  {
    code: "irish-breakfast",
    key: "irish-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.irishBreakfast,
  },
  {
    code: "israeli-breakfast",
    key: "israeli-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.israeliBreakfast,
  },
  {
    code: "japanese-breakfast",
    key: "japanese-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.japaneseBreakfast,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "lunch",
    key: "lunch",
    text: HotelRoomOfferBoardType.lunch,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "nomeal",
    key: "nomeal",
    text: HotelRoomOfferBoardType.none,
  },
  {
    code: "scandinavian-breakfast",
    key: "scandinavian-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.scandinavianBreakfast,
  },
  {
    code: "scottish-breakfast",
    key: "scottish-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.scottishBreakfast,
  },
  {
    code: "soft-all-inclusive",
    key: "soft-all-inclusive",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.softAllInclusive,
  },
  {
    code: "some-meal",
    key: "some-meal",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.someMeal,
  },
  {
    code: "super-all-inclusive",
    key: "super-all-inclusive",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.superAllInclusive,
  },
  {
    code: "ultra-all-inclusive",
    key: "ultra-all-inclusive",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.ultraAllInclusive,
  },
  {
    code: "2096",
    key: "2096",
    text: "Eligible for hotel loyalty points",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2097",
    key: "2097",
    text: "Free high-speed internet",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2098",
    key: "2098",
    text: "Free breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2102",
    key: "2102",
    text: "All meals",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2103",
    key: "2103",
    text: "Continental breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2104",
    key: "2104",
    text: "Full Breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2105",
    key: "2105",
    text: "English breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2106",
    key: "2106",
    text: "Free lunch",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2107",
    key: "2107",
    text: "Free dinner",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2108",
    key: "2108",
    text: "Food/beverage credit or discount",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2109",
    key: "2109",
    text: "Free parking",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2110",
    key: "2110",
    text: "Extended parking",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2111",
    key: "2111",
    text: "All-inclusive (food/beverages/snacks)",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2175",
    key: "2175",
    text: "Same-day cancellation",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2191",
    key: "2191",
    text: "Free high-speed internet",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2192",
    key: "2192",
    text: "Free WiFi",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2193",
    key: "2193",
    text: "Continental breakfast for 2",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2194",
    key: "2194",
    text: "Breakfast for 2",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2195",
    key: "2195",
    text: "Free valet parking",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2196",
    key: "2196",
    text: "Free airport shuttle",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2197",
    key: "2197",
    text: "Free room upgrade (subject to availability)",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2198",
    key: "2198",
    text: "Resort credit included",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2199",
    key: "2199",
    text: "Welcome gift upon arrival",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2200",
    key: "2200",
    text: "Spa Credit",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2201",
    key: "2201",
    text: "Golf credit",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2202",
    key: "2202",
    text: "VIP line access to nightclub(s)",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2203",
    key: "2203",
    text: "2-for-1 buffet",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2204",
    key: "2204",
    text: "Free ski lift ticket & rental",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2205",
    key: "2205",
    text: "Breakfast buffet",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2206",
    key: "2206",
    text: "Half board",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2207",
    key: "2207",
    text: "Full board",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2208",
    key: "2208",
    text: "Full kitchen",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2209",
    key: "2209",
    text: "Continental breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2210",
    key: "2210",
    text: "Full breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2211",
    key: "2211",
    text: "Breakfast buffet",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2212",
    key: "2212",
    text: "Free fitness center access",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2213",
    key: "2213",
    text: "Free business center access",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2214",
    key: "2214",
    text: "Free airport shuttle",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2215",
    key: "2215",
    text: "Free hotel parking",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2216",
    key: "2216",
    text: "Free valet parking",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2217",
    key: "2217",
    text: "Welcome drink upon arrival",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2218",
    key: "2218",
    text: "Free local calls",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2219",
    key: "2219",
    text: "Complimentary wine reception",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2220",
    key: "2220",
    text: "Free WiFi",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2221",
    key: "2221",
    text: "Free local shuttle",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2222",
    key: "2222",
    text: "Free local newspaper",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2224",
    key: "2224",
    text: "Room upgrade upon availability",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2225",
    key: "2225",
    text: "Guaranteed room upgrade",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2226",
    key: "2226",
    text: "Early check-in privilege",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2227",
    key: "2227",
    text: "Late check-out privilege",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2228",
    key: "2228",
    text: "Free bottled water",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2233",
    key: "2233",
    text: "Food and beverage discount",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2234",
    key: "2234",
    text: "Slot play",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2235",
    key: "2235",
    text: "Casino credit",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "2236",
    key: "2236",
    text: "Match play",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742551",
    key: "1073742551",
    text: "Free welcome drink",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742552",
    key: "1073742552",
    text: "Drinks and hors d'oeuvres",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742617",
    key: "1073742617",
    text: "Disney park tickets",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742618",
    key: "1073742618",
    text: "Spa access",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742621",
    key: "1073742621",
    text: "Free breakfast for 1 adult",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742622",
    key: "1073742622",
    text: "Upgrade to club floor",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742623",
    key: "1073742623",
    text: "Upgrade to business floor",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742624",
    key: "1073742624",
    text: "Complimentary minibar items",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742625",
    key: "1073742625",
    text: "Free dinner",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742626",
    key: "1073742626",
    text: "Free dinner for 1 adult",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742627",
    key: "1073742627",
    text: "Full kitchen",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742680",
    key: "1073742680",
    text: "1000 Choice Privileges points per night",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742786",
    key: "1073742786",
    text: "Free breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742787",
    key: "1073742787",
    text: "Free internet",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742857",
    key: "1073742857",
    text: "Breakfast for 1",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742858",
    key: "1073742858",
    text: "Complimentary green fees",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742859",
    key: "1073742859",
    text: "Free one-way airport transfer",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742860",
    key: "1073742860",
    text: "Free round-trip airport transfer",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742861",
    key: "1073742861",
    text: "Free water park passes",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742862",
    key: "1073742862",
    text: "2 game drives per night",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742863",
    key: "1073742863",
    text: "1 game drive per night",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073742909",
    key: "1073742909",
    text: "City tax included",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073743274",
    key: "1073743274",
    text: "Early check-in (subject to availability)",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073743275",
    key: "1073743275",
    text: "Late check-out (subject to availability)",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073743286",
    key: "1073743286",
    text: "Free massage included",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073743287",
    key: "1073743287",
    text: "Free minibar",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073743288",
    key: "1073743288",
    text: "Ski pass included",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073743389",
    key: "1073743389",
    text: "Resort fee waived",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073743391",
    key: "1073743391",
    text: "Chain loyalty rate",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073743424",
    key: "1073743424",
    text: "Fine Hotels and Resorts",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073743425",
    key: "1073743425",
    text: "The Hotel Collection",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073744459",
    key: "1073744459",
    text: "Coffee/Pastries Each Morning",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073744734",
    key: "1073744734",
    text: "Japanese-style breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073744735",
    key: "1073744735",
    text: "Taiwanese-style breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073744736",
    key: "1073744736",
    text: "Vegetarian dinner",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073744737",
    key: "1073744737",
    text: "Chinese-style dinner",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073744738",
    key: "1073744738",
    text: "Kaiseki dinner",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073744739",
    key: "1073744739",
    text: "Kaiseki seafood dinner",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073744750",
    key: "1073744750",
    text: "Kaiseki meat dinner",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073744752",
    key: "1073744752",
    text: "Buffet dinner",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073744753",
    key: "1073744753",
    text: "Half board (Chinese-style dinner)",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073744754",
    key: "1073744754",
    text: "Half board (kaiseki dinner)",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073744755",
    key: "1073744755",
    text: "Half board (buffet dinner)",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745241",
    key: "1073745241",
    text: "Amex reserve",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745259",
    key: "1073745259",
    text: "1 COVID-19 RT-PCR test per person",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745260",
    key: "1073745260",
    text: "2 COVID-19 RT-PCR tests per person",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745419",
    key: "1073745419",
    text: "Partner Negotiated Rate",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745446",
    key: "1073745446",
    text: "Tickets for both Disney parks valid for the entire stay",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745508",
    key: "1073745508",
    text: "Group Rate",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745568",
    key: "1073745568",
    text: "Afternoon tea",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745570",
    key: "1073745570",
    text: "Extended parking - 4 days of parking",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745571",
    key: "1073745571",
    text: "Extended parking - 7 days of parking",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745572",
    key: "1073745572",
    text: "Extended parking - 10 days of parking",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745573",
    key: "1073745573",
    text: "Extended parking - 14 days of parking",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745653",
    key: "1073745653",
    text: "Breakfast for up to 2 adults",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745654",
    key: "1073745654",
    text: "Half board (breakfast and dinner)",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745655",
    key: "1073745655",
    text: "Half board (breakfast and lunch)",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
  {
    code: "1073745656",
    key: "1073745656",
    text: "Ramadan meal (suhoor)",
    icon: "/assets/v2/icons/mug-outline.svg",
  },
];

export const NETWORK_RPC_PROVIDES: { [key: string]: string } = {
  ETH: `https://mainnet.infura.io/v3/${publicEnvConfig.infuraProjectId}`,
  BASE: "https://mainnet.base.org",
};
